import { Button, Input, Text } from '@ui/themed';
import { t } from 'i18next';

export function ListEditor({
  label,
  description,
  values,
  onChange,
  placeholder,
  addLabel,
  emptyText,
  errors,
}: {
  label: string;
  description?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  emptyText?: string;
  /** Parallel to `values`; the message shown under a given row, if any. */
  errors?: (string | undefined)[];
}) {
  const list = Array.isArray(values) ? values : [];
  const lastValue = list[list.length - 1];
  const canAdd = lastValue === undefined || lastValue.trim() !== '';

  function updateAt(index: number, value: string) {
    onChange(list.map((current, i) => (i === index ? value : current)));
  }

  function removeAt(index: number) {
    onChange(list.filter((_, i) => i !== index));
  }

  function add() {
    if (!canAdd) {
      return;
    }

    onChange([...list, '']);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <Text>{label}</Text>
        {description && (
          <Text size="sm" variant="secondary">
            {description}
          </Text>
        )}
      </div>

      {list.length === 0 && (
        <Text size="sm" variant="secondary">
          {emptyText ?? t('features:storefront_options.components.list_editor.empty')}
        </Text>
      )}

      {list.map((value, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
        <div className="flex items-center gap-2" key={index}>
          <div className="flex-1">
            <Input
              value={value}
              placeholder={placeholder}
              error={errors?.[index]}
              onChange={(next) => updateAt(index, next)}
            />
          </div>
          <Button
            variant="dangerBare"
            icon="delete"
            ariaLabel={t('features:storefront_options.components.list_editor.remove')}
            onClick={() => removeAt(index)}
          />
        </div>
      ))}

      <div>
        <Button variant="secondary" icon="plusCircle" disabled={!canAdd} onClick={add}>
          {addLabel ?? t('features:storefront_options.components.list_editor.add')}
        </Button>
      </div>
    </div>
  );
}
